import { Component, effect, inject, input, output, signal } from '@angular/core';
import {
  FilterValues,
  TableColumnFilterOption,
  TableRowFilter,
} from 'common/table/filtering-support.interface';
import { ActivatedRoute } from '@angular/router';
import { MatInput, MatSuffix } from '@angular/material/input';
import { MatFormField } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { form, FormField } from '@angular/forms/signals';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { IconDirective } from 'common/icon/icon.directive';
import { ButtonDirective } from 'common/button/button.directive';

@Component({
  selector: 'comp-paged-table-filters',
  templateUrl: 'paged-table-filters.component.html',
  styleUrls: ['./paged-table-filters.component.scss'],
  imports: [
    MatFormField,
    MatSuffix,
    MatInput,
    TranslateModule,
    MatSelect,
    FormField,
    MatOption,
    MatButtonModule,
    MatCheckbox,
    MatMenuTrigger,
    MatMenu,
    IconDirective,
    ButtonDirective,
  ],
})
export class PagedTableFiltersComponent {
  private readonly route = inject(ActivatedRoute);

  filters = input<TableRowFilter[]>([]);

  filtersChanged = output<FilterValues>();
  columnsChanged = output<string[]>();

  readonly model = signal<FilterValues>({});
  readonly form = form(this.model);

  readonly isInitialized = signal(false);

  constructor() {
    effect((onCleanup) => {
      const value = this.model();

      if (!this.isInitialized()) {
        return;
      }

      const timeout = window.setTimeout(() => {
        this.filtersChanged.emit(this.getNonColumnFilterValues(value));
      }, 500);

      onCleanup(() => window.clearTimeout(timeout));
    });
    effect(() => {
      const initialModel = this.filters().reduce<FilterValues>((result, filter) => {
        if (filter.type === 'columns') {
          result[filter.id] =
            this.route.snapshot.queryParams[filter.id] ??
            this.getColumnOptions(filter)
              .map((column) => column.id)
              .join(',');
          return result;
        }

        result[filter.id] = this.route.snapshot.queryParams[filter.id] ?? '';
        return result;
      }, {});

      this.model.set(initialModel);
      this.isInitialized.set(true);
    });
  }

  getEnumIdentifiers(filter: TableRowFilter): [string, string][] {
    return Object.entries(filter.enumType ?? {});
  }

  getColumnOptions(filter: TableRowFilter): TableColumnFilterOption[] {
    return filter.columnOptions ?? [];
  }

  isColumnSelected(filter: TableRowFilter, columnId: string): boolean {
    return this.getSelectedColumnIds(filter.id).includes(columnId);
  }

  toggleColumn(filter: TableRowFilter, columnId: string, checked: boolean): void {
    const selectedColumnIds = new Set(this.getSelectedColumnIds(filter.id));

    if (checked) {
      selectedColumnIds.add(columnId);
    } else {
      selectedColumnIds.delete(columnId);
    }

    const orderedSelectedColumnIds = this.getColumnOptions(filter)
      .map((column) => column.id)
      .filter((id) => selectedColumnIds.has(id));

    this.model.update((value) => ({
      ...value,
      [filter.id]: orderedSelectedColumnIds.join(','),
    }));

    this.columnsChanged.emit(orderedSelectedColumnIds);
  }

  private getSelectedColumnIds(filterId: string): string[] {
    return (this.model()[filterId] ?? '').split(',').filter(Boolean);
  }

  clearFilter(id: string): void {
    this.model.update((value) => ({
      ...value,
      [id]: '',
    }));
  }

  // shown columns is also implemented as filter
  // this methods returns the values of all filters except the ones for the columns to show
  private getNonColumnFilterValues(value: FilterValues): FilterValues {
    return Object.fromEntries(
      Object.entries(value).filter(([key]) => key !== 'columns'),
    ) as FilterValues;
  }
}
